import React from 'react';
import { FileText, AlertTriangle, Users, Shield, Gavel, Phone } from 'lucide-react';

export const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Conditions Générales d'Utilisation</h1>
          </div>
          <p className="text-gray-600">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Objet et acceptation</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation de la 
              plateforme DPEWizard, accessible à l'adresse dpewizard.com, qui propose des services 
              d'analyse de Diagnostics de Performance Énergétique (DPE) basés sur les données 
              officielles de l'ADEME.
            </p>
            <p className="text-gray-700 leading-relaxed">
              L'utilisation de nos services implique l'acceptation pleine et entière des présentes CGU. 
              Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser nos services.
            </p>
          </section>

          {/* Définitions */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Définitions</h2>
            <ul className="space-y-3 text-gray-700">
              <li><strong>« Service » :</strong> La plateforme DPEWizard et toutes ses fonctionnalités</li>
              <li><strong>« Utilisateur » :</strong> Toute personne utilisant les services DPEWizard</li>
              <li><strong>« Compte » :</strong> Espace personnel créé par l'Utilisateur</li>
              <li><strong>« DPE » :</strong> Diagnostic de Performance Énergétique</li>
              <li><strong>« ADEME » :</strong> Agence de l'Environnement et de la Maîtrise de l'Énergie</li>
            </ul>
          </section>

          {/* Description du service */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Users className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">3. Description du service</h2>
            </div>
            
            <p className="text-gray-700 mb-4">DPEWizard propose les services suivants :</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Analyse automatique de fichiers DPE</li>
              <li>Récupération de données officielles ADEME</li>
              <li>Détection d'incohérences dans les DPE</li>
              <li>Génération de recommandations personnalisées</li>
              <li>Questionnaires contextuels pour affiner l'analyse</li>
              <li>Tableau de bord et historique des analyses</li>
            </ul>
          </section>

          {/* Inscription et compte */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Inscription et compte utilisateur</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">4.1 Création de compte</h3>
                <p className="text-gray-700">
                  L'inscription nécessite la fourniture d'une adresse email valide et la création 
                  d'un mot de passe sécurisé. Vous vous engagez à fournir des informations exactes 
                  et à les maintenir à jour.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">4.2 Responsabilité du compte</h3>
                <p className="text-gray-700">
                  Vous êtes seul responsable de la confidentialité de vos identifiants et de toutes 
                  les activités effectuées sous votre compte. Vous devez nous informer immédiatement 
                  de toute utilisation non autorisée de votre compte.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">4.3 Suspension et résiliation</h3>
                <p className="text-gray-700">
                  Nous nous réservons le droit de suspendre ou résilier votre compte en cas de 
                  non-respect des présentes CGU, sans préavis ni indemnité.
                </p>
              </div>
            </div>
          </section>

          {/* Utilisation du service */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">5. Utilisation du service</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">5.1 Usage autorisé</h3>
                <p className="text-gray-700 mb-2">Vous vous engagez à utiliser nos services uniquement pour :</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>L'analyse légitime de DPE dont vous détenez les droits</li>
                  <li>Des fins professionnelles ou personnelles légales</li>
                  <li>Le respect de la réglementation en vigueur</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">5.2 Usages interdits</h3>
                <p className="text-gray-700 mb-2">Il est strictement interdit de :</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Utiliser le service à des fins frauduleuses ou illégales</li>
                  <li>Tenter de contourner les mesures de sécurité</li>
                  <li>Surcharger nos serveurs par des requêtes excessives</li>
                  <li>Reproduire, distribuer ou revendre nos services sans autorisation</li>
                  <li>Analyser des DPE sans autorisation du propriétaire</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Données et confidentialité */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Données et confidentialité</h2>
            
            <p className="text-gray-700 mb-4">
              Le traitement de vos données personnelles est régi par notre 
              <a href="/privacy" className="text-blue-600 hover:underline"> Politique de Confidentialité</a>.
            </p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">6.1 Données DPE</h3>
                <p className="text-gray-700">
                  Les fichiers DPE que vous téléchargez sont traités de manière confidentielle. 
                  Nous nous engageons à ne pas les partager avec des tiers sans votre consentement.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">6.2 Données ADEME</h3>
                <p className="text-gray-700">
                  Nos analyses utilisent les données officielles ADEME, qui sont publiques et 
                  accessibles à tous conformément à la réglementation.
                </p>
              </div>
            </div>
          </section>

          {/* Propriété intellectuelle */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Propriété intellectuelle</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">7.1 Nos droits</h3>
                <p className="text-gray-700">
                  L'ensemble du contenu de DPEWizard (design, algorithmes, textes, logos) est 
                  protégé par les droits de propriété intellectuelle. Toute reproduction non 
                  autorisée est interdite.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">7.2 Vos droits</h3>
                <p className="text-gray-700">
                  Vous conservez tous les droits sur les DPE que vous téléchargez. Les analyses 
                  et recommandations générées vous appartiennent et peuvent être utilisées librement.
                </p>
              </div>
            </div>
          </section>

          {/* Responsabilité */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              <h2 className="text-2xl font-semibold text-gray-900">8. Responsabilité et garanties</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">8.1 Limitation de responsabilité</h3>
                <p className="text-gray-700">
                  DPEWizard s'efforce de fournir des analyses précises, mais ne peut garantir 
                  l'exactitude absolue des résultats. Les recommandations sont fournies à titre 
                  informatif et ne remplacent pas l'avis d'un professionnel qualifié.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">8.2 Disponibilité du service</h3>
                <p className="text-gray-700">
                  Nous nous efforçons d'assurer une disponibilité maximale de nos services, mais 
                  ne pouvons garantir un fonctionnement ininterrompu. Des maintenances programmées 
                  peuvent occasionner des interruptions temporaires.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">8.3 Responsabilité de l'utilisateur</h3>
                <p className="text-gray-700">
                  Vous êtes responsable de l'utilisation que vous faites des résultats d'analyse 
                  et des décisions prises sur cette base. Nous recommandons de toujours consulter 
                  un professionnel pour des projets importants.
                </p>
              </div>
            </div>
          </section>

          {/* Tarification */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Tarification et paiement</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">9.1 Services gratuits</h3>
                <p className="text-gray-700">
                  Certaines fonctionnalités de base sont disponibles gratuitement avec des 
                  limitations d'usage (nombre d'analyses, fonctionnalités avancées).
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">9.2 Services payants</h3>
                <p className="text-gray-700">
                  Les tarifs des services premium sont disponibles sur notre page 
                  <a href="/pricing" className="text-blue-600 hover:underline"> Tarifs</a>. 
                  Les prix sont indiqués en euros TTC et peuvent être modifiés avec un préavis de 30 jours.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">9.3 Remboursement</h3>
                <p className="text-gray-700">
                  Sauf cas particuliers, les paiements effectués ne sont pas remboursables. 
                  En cas de dysfonctionnement majeur de nos services, nous étudions les demandes 
                  de remboursement au cas par cas.
                </p>
              </div>
            </div>
          </section>

          {/* Modifications */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Modifications des CGU</h2>
            
            <p className="text-gray-700">
              Nous nous réservons le droit de modifier les présentes CGU à tout moment. 
              Les utilisateurs seront informés des modifications importantes par email ou 
              notification sur la plateforme. L'utilisation continue de nos services après 
              modification vaut acceptation des nouvelles conditions.
            </p>
          </section>

          {/* Résiliation */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Résiliation</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">11.1 Par l'utilisateur</h3>
                <p className="text-gray-700">
                  Vous pouvez supprimer votre compte à tout moment depuis les paramètres de votre 
                  profil ou en nous contactant à support@dpewizard.com.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">11.2 Par DPEWizard</h3>
                <p className="text-gray-700">
                  Nous pouvons résilier votre accès en cas de violation des présentes CGU, avec 
                  ou sans préavis selon la gravité de la violation.
                </p>
              </div>
            </div>
          </section>

          {/* Droit applicable */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Gavel className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">12. Droit applicable et juridiction</h2>
            </div>
            
            <p className="text-gray-700">
              Les présentes CGU sont régies par le droit français. En cas de litige, les parties 
              s'efforceront de trouver une solution amiable. À défaut, les tribunaux français 
              seront seuls compétents.
            </p>
          </section>

          {/* Contact */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Phone className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-900">13. Contact</h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              Pour toute question concernant ces CGU ou nos services, contactez-nous :
            </p>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">
                <strong>Email :</strong>{' '}
                <a href="mailto:support@dpewizard.com" className="text-blue-600 hover:underline">
                  support@dpewizard.com
                </a>
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Société :</strong> DPEWizard
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Site web :</strong>{' '}
                <a href="https://dpewizard.com" className="text-blue-600 hover:underline">
                  dpewizard.com
                </a>
              </p>
            </div>
          </section>

          {/* Footer mentions légales */}
          <section className="border-t pt-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Important :</strong> Ces conditions générales d'utilisation constituent 
                un contrat entre vous et DPEWizard. En utilisant nos services, vous acceptez 
                d'être lié par ces termes. Si vous avez des questions, n'hésitez pas à nous contacter.
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Terms;